import { UpOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Collapse, Divider, Flex, Tabs, TabsProps, Typography } from 'antd';
import Sider from 'antd/es/layout/Sider';
import dayjs from 'dayjs';
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getTopicAttachments } from 'src/apis/helpdesk/topic.api';
import { TopicContext } from 'src/contexts/topic.context';
import useScreenSize from 'src/hooks/useScreenSize';
import { TopicAttachmentFile, TopicAttachmentMedia } from 'src/pages/v2/Topic/TopicConversationBox/Attachment';
import { IFilePath, ITopicMember } from 'src/types/helpdesk/topic.type';
import { groupByAttachment } from 'src/utils/utils';
import TopicAbout from '../About';
import Activity from '../Activity';
import { TopicMember } from '../Member';
import SearchMessage from '../SearchMessage';
import './TopicSider.scss';
import { useChatStore } from 'src/stores/chatStore';
const { Text } = Typography;

interface CollapseType {
  openAddMember: boolean;
  setOpenAddMember: Dispatch<SetStateAction<boolean>>;
}

const tabItems: (
  topicId: string,
  { setOpenAddMember }: CollapseType,
  images: IFilePath[],
  files: IFilePath[],
  loadingAttachment: boolean,
  members: ITopicMember[],
  loadingMember: boolean,
  showSearch: boolean,
  setShowSearch: React.Dispatch<React.SetStateAction<boolean>>
) => TabsProps['items'] = (
  topicId,
  { setOpenAddMember },
  images,
  files,
  loadingAttachment,
  members,
  loadingMember,
  showSearch,
  setShowSearch
) => {
  return [
    {
      key: '1',
      label: 'Chi tiết',
      children: (
        <div style={{ height: 'calc(var(--body-topic-height) - 62px)', overflowY: 'auto', paddingBlock: 16 }}>
          {showSearch ? (
            <SearchMessage showSearch={showSearch} setShowSearch={setShowSearch} />
          ) : (
            <Flex vertical>
              <TopicAbout topicId={topicId} setOpenAddMember={setOpenAddMember} setShowSearch={setShowSearch} />
              <Divider style={{ marginBlockEnd: 8 }} />
              <Collapse
                ghost
                defaultActiveKey={'topic-member-list'}
                expandIconPosition='end'
                expandIcon={({ isActive }) => <UpOutlined rotate={isActive ? 180 : 90} />}
                items={[
                  {
                    key: 'topic-member-list',
                    label: <Text strong>Thành viên nhóm ({members?.length || 0})</Text>,
                    children: <TopicMember members={members || []} loading={loadingMember} />,
                    className: 'topic-member-list'
                  }
                ]}
              />
              <Divider style={{ marginBlock: 8 }} />
              <Collapse
                ghost
                expandIconPosition='end'
                expandIcon={({ isActive }) => <UpOutlined rotate={isActive ? 180 : 90} />}
                items={[
                  {
                    key: 'topic-attachment-media',
                    label: <Text strong>Ảnh/Video ({images.length || 0})</Text>,
                    children: <TopicAttachmentMedia images={images} loading={loadingAttachment} />,
                    className: 'topic-attachment-media'
                  }
                ]}
              />
              <Divider style={{ marginBlock: 8 }} />
              <Collapse
                ghost
                expandIconPosition='end'
                expandIcon={({ isActive }) => <UpOutlined rotate={isActive ? 180 : 90} />}
                items={[
                  {
                    key: 'topic-attachment-file',
                    label: <Text strong>File ({files.length || 0})</Text>,
                    children: <TopicAttachmentFile files={files} loading={loadingAttachment} />,
                    className: 'topic-attachment-file'
                  }
                ]}
              />
            </Flex>
          )}
        </div>
      )
    },
    {
      key: '2',
      label: 'Hoạt động',
      children: (
        <div style={{ height: 'calc(var(--body-topic-height) - 62px)', overflowY: 'auto', paddingBlock: 16 }}>
          <Activity />
        </div>
      )
    }
  ];
};

export default function TopicSider() {
  const { isConversationSiderCollapsed, setIsConversationSiderCollapsed, topicAttachments, setTopicAttachments } =
    useContext(TopicContext);

  const [openAddMember, setOpenAddMember] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState(false);
  const [images, setImages] = useState<IFilePath[]>([]);
  const [files, setFiles] = useState<IFilePath[]>([]);
  const [canAccessTopic, setCanAccessTopic] = useState<boolean>(false);
  const { id } = useParams();
  const rooms = useChatStore((state) => state.rooms);
  const currentRoom = rooms[id as string];
  const currentTopicMembers = currentRoom?.topicMembers || [];
  const screen = useScreenSize();

  const topicAttachmentQuery = useQuery({
    queryKey: ['topicAttachments', String(id)],
    queryFn: () => getTopicAttachments(String(id)),
    enabled: !!id && !!canAccessTopic,
    staleTime: 60 * 1000
  });

  useEffect(() => {
    if (screen < 1200) {
      setIsConversationSiderCollapsed(true);
    }
  }, [screen, setIsConversationSiderCollapsed]);

  useEffect(() => {
    if (!id) {
      setIsConversationSiderCollapsed(true);
    }
  }, [id, setIsConversationSiderCollapsed]);

  useEffect(() => {
    if (id) {
      const hasAccess = !!currentRoom;

      setCanAccessTopic(hasAccess);
    }
  }, [id, currentRoom]);

  useEffect(() => {
    if (topicAttachmentQuery?.data) {
      setTopicAttachments(topicAttachmentQuery?.data.data);
    }
  }, [setTopicAttachments, topicAttachmentQuery?.data]);

  useEffect(() => {
    const topicAttachment = groupByAttachment(topicAttachments);
    setImages(topicAttachment.images.sort((a, b) => dayjs(b.createdDate).valueOf() - dayjs(a.createdDate).valueOf()));
    setFiles(topicAttachment.files.sort((a, b) => dayjs(b.createdDate).valueOf() - dayjs(a.createdDate).valueOf()));
  }, [topicAttachments]);

  return (
    <Sider
      className='topic-sider'
      trigger={null}
      collapsible
      collapsed={isConversationSiderCollapsed}
      style={{ background: '#fff' }}
      collapsedWidth={0}
      width={350}
    >
      {id && (
        <Tabs
          defaultActiveKey='1'
          items={tabItems(
            id,
            { openAddMember, setOpenAddMember },
            images,
            files,
            topicAttachmentQuery.isFetching,
            currentTopicMembers || [],
            false,
            showSearch,
            setShowSearch
          )}
          tabBarStyle={{ height: '62px', width: '100%', marginBottom: 0 }}
        />
      )}
    </Sider>
  );
}
